import {
  goalMetricMetadataMap,
  GoalMetricUnit,
  GoalOutcome,
  VerticalOption,
  Goal,
  GoalPeriodType,
  GoalStatus,
  GoalMetricMetadata,
  GoalMetricOption,
} from '@amzn/aet-galactus-kit';
import { GoalMetricDefaultName } from './displayTypes';
import { GoalDisplay, goalSortableColumns } from './types';
import { TagType } from '@amzn/meridian/tag/tag';
import { TableSortDirection } from '@amzn/meridian/table/table';
import { SortDirection } from '../../Types/SortDirection';
import { Profile } from '../../Types';
import { getLocaleStringFormatted } from '../../translations';
import { Feature, isFeatureEnabled } from '../../config/FeaturesConfig';
import {
  CoachingFormData,
  DeliveredStatusObject,
} from '../../api/CoachingForm/schema';
import {
  getAllSecurityOptions,
  getAllVerticals,
} from '../CoachingForm/ContactReview/Data';
import {
  getAllMetrics,
  getAllTopics,
  getAllTenets,
  getAllBehaviors,
} from '../CoachingForm/CoachingDetails/Data';
import {
  getAllDurations,
  getAllMethods,
  getAllPreparationTimes,
  getAllTactics,
} from '../CoachingForm/CoachingSession/Data';
import { getAllReasonOptions } from '../CoachingForm/NoCoaching/Data';
import { getLabelFromValue } from '../CoachingForm/FieldComponents/Helpers';
import moment from 'moment';

export enum GoalPage {
  DASHBOARD = 'dashboard',
  METRICS = 'metrics',
  COACHING = 'coaching-notes',
}

export interface Toast {
  id: string;
  type: 'success' | 'error';
}

export enum GoalTableColumn {
  GOAL = 'GOAL',
  TARGET = 'TARGET',
  TIME_RANGE = 'TIME_RANGE',
  STATUS = 'STATUS',
  OUTCOME = 'OUTCOME',
  CREATED_BY = 'CREATED_BY',
  COMMENTS = 'COMMENTS',
  ASSOCIATE = 'ASSOCIATE',
  COACHING = 'COACHING',
}

export enum GoalTab {
  TEAM = 'TEAM',
  ASSOCIATE = 'ASSOCIATE',
  NO_TAB = 'NO_TAB',
}

export enum GoalCreationInputs {
  GOAL_TYPE = 'GOAL_TYPE',
  ASSOCIATE = 'ASSOCIATE',
  VERTICAL = 'VERTICAL',
  METRIC = 'METRIC',
  TARGET_VALUE = 'TARGET_VALUE',
  GOAL_DESCRIPTION = 'GOAL_DESCRIPTION',
  TIME_VALUE = 'TIME_VALUE',
}

export const GOAL_DESCRIPTION_SIZE_LIMIT = 50;
export const GOAL_JUSTIFICATION_SIZE_LIMIT = 500;
export const GOAL_COMMENT_SIZE_LIMIT = 500;

export const showColumn = (
  column: GoalTableColumn,
  currentTab: GoalTab = GoalTab.NO_TAB,
  isTmViewing: boolean = false,
  goalSettingTeamEnabled: boolean = false,
  isOnTmPage: boolean = false
): boolean => {
  switch (column) {
    // basic columns displayed everywhere
    case GoalTableColumn.GOAL:
    case GoalTableColumn.TARGET:
    case GoalTableColumn.TIME_RANGE:
    case GoalTableColumn.STATUS:
    case GoalTableColumn.OUTCOME:
    case GoalTableColumn.CREATED_BY:
    case GoalTableColumn.COMMENTS:
      return true;
    case GoalTableColumn.ASSOCIATE:
      // only show Associate on TM's associate tab
      return goalSettingTeamEnabled
        ? currentTab === GoalTab.ASSOCIATE
        : isOnTmPage;
    case GoalTableColumn.COACHING:
      // TM can see coaching column in goal section on CSA's pages
      // TM can see coaching column on their own associate tab
      // CSA can never see coaching column
      return currentTab !== GoalTab.TEAM && isTmViewing;
    default:
      return false;
  }
};

const getMonthTranslation = (monthInNumber: number): string => {
  if (monthInNumber < 1 || monthInNumber > 12) {
    throw new Error('Month number must be between 1 and 12');
  }
  const date = new Date(new Date().getFullYear(), monthInNumber - 1, 1);
  return new Intl.DateTimeFormat(getLocaleStringFormatted(), {
    month: 'long',
  }).format(date);
};

export const getTimestampTranslation = (timestamp?: string): string => {
  if (!timestamp) return '';
  return Intl.DateTimeFormat(getLocaleStringFormatted(), {
    dateStyle: 'full',
    timeStyle: 'long',
  }).format(new Date(timestamp));
};

export const getRecentMonths = (count: number) => {
  const result: { year: number; month: string; monthNum: number }[] = [];
  const currentDate = moment();

  // Handle positive and negative counts differently
  if (count > 0) {
    // For positive numbers, include current month and future months
    for (let i = 0; i < count; i++) {
      const newDate = currentDate.clone().add(i, 'months');
      populateResult(result, newDate);
    }
  } else {
    // For negative numbers, include previous months and current month
    for (let i = count + 1; i <= 0; i++) {
      const newDate = currentDate.clone().add(i - 1, 'months');
      populateResult(result, newDate);
    }
    result.reverse();
  }

  return result;
};

const populateResult = (
  result: { year: number; month: string; monthNum: number }[],
  newDate: moment.Moment
) => {
  result.push({
    year: newDate.year(),
    month: Intl.DateTimeFormat(getLocaleStringFormatted(), {
      month: 'long',
    }).format(newDate.toDate()),
    monthNum: newDate.month() + 1,
  });
};

export const getMetricStringIdAndDefaultMessage = (
  metric: string
): {
  id: string;
  defaultMessage: string;
} => {
  const stringIdAndDefaultMessage = GoalMetricDefaultName.get(metric);
  return {
    id: stringIdAndDefaultMessage?.[0] ?? '',
    defaultMessage: stringIdAndDefaultMessage?.[1] ?? metric,
  };
};

export const getVerticalOptionFromValue = (
  value: string
): VerticalOption | undefined => {
  return (Object.values(VerticalOption) as string[]).includes(value)
    ? (value as VerticalOption)
    : undefined;
};

//Filters goal metrics based on feature flag settings
export const filterGoalMetricsByFeatureFlags = (
  metrics: GoalMetricMetadata[],
  enableACHTHoldTime: boolean
): GoalMetricMetadata[] => {
  if (enableACHTHoldTime) {
    return metrics;
  }

  return metrics.filter(
    metric =>
      metric.metricName !== GoalMetricOption.ACHT &&
      metric.metricName !== GoalMetricOption.HOLD_TIME
  );
};

/** CCX-related GoalMetricOption values to remove when enableCCXRemoval feature flag is active */
export const CCX_GOAL_METRICS_TO_REMOVE = new Set<string>([
  GoalMetricOption.CCX,
  GoalMetricOption.CCX_NEG,
  GoalMetricOption.CCX_NEG_ONEBOX,
]);

/**
 * Filters out CCX-related goal metrics when the enableCCXRemoval feature flag is enabled.
 * Used for filtering metric options in goal creation and filtering existing goals from display.
 */
export const filterCCXGoalMetrics = (
  metrics: GoalMetricMetadata[]
): GoalMetricMetadata[] => {
  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);
  if (!enableCCXRemoval) return metrics;

  return metrics.filter(
    metric => !CCX_GOAL_METRICS_TO_REMOVE.has(metric.metricName)
  );
};

/**
 * Filters out goals with CCX-related metrics when the enableCCXRemoval feature flag is enabled.
 * Used for hiding existing CCX goals from the goals display table.
 */
export const filterCCXGoals = (goals: GoalDisplay[]): GoalDisplay[] => {
  const enableCCXRemoval = isFeatureEnabled(Feature.ENABLE_CCX_REMOVAL);
  if (!enableCCXRemoval) return goals;

  return goals.filter(goal => !CCX_GOAL_METRICS_TO_REMOVE.has(goal.metric));
};

export const formatGoalTargetValueForAPI = (
  selectedMetric: GoalMetricMetadata | undefined,
  targetValue: string,
  primaryUnit: string,
  secondaryUnit: string
): string => {
  if (
    selectedMetric?.metricUnit === GoalMetricUnit.MIN_SEC ||
    selectedMetric?.metricUnit === GoalMetricUnit.HOUR_MIN
  ) {
    const primaryVal = primaryUnit || '0';
    const secondaryVal = secondaryUnit || '0';
    return `${primaryVal}:${secondaryVal.padStart(2, '0')}`;
  }
  return targetValue;
};

export const goalOutcomeMap: { [key in GoalOutcome]: TagType } = {
  [GoalOutcome.Met]: 'success',
  [GoalOutcome.Unmet]: 'warning',
};

const convertToSeconds = (timeString: string) => {
  const parts = timeString.split(':');

  const minutes = parseInt(parts[0], 10) || 0;
  const seconds = parseInt(parts[1], 10) || 0;

  return minutes * 60 + seconds;
};

export const goalSort = (
  key: keyof GoalDisplay,
  sortDirection: TableSortDirection
) => {
  return (a: GoalDisplay, b: GoalDisplay) => {
    const valueA = a[key];
    const valueB = b[key];

    if (key !== goalSortableColumns.Outcome) {
      if (valueA === undefined && valueB === undefined) {
        return 0;
      } else if (valueA === undefined) {
        return 1;
      } else if (valueB === undefined) {
        return -1;
      }
    }
    let convertedValueA, convertedValueB, sortResult;

    switch (key) {
      case goalSortableColumns.Coaching:
        convertedValueA = a.coachingIDs?.length ?? 0;
        convertedValueB = b.coachingIDs?.length ?? 0;
        if (convertedValueA == 0 && convertedValueB == 0) {
          convertedValueA = Number(a.status !== GoalStatus.Closed);
          convertedValueB = Number(b.status !== GoalStatus.Closed);
        }
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        break;

      case goalSortableColumns.PeriodValue:
        convertedValueA = Date.parse(a.endDate);
        convertedValueB = Date.parse(b.endDate);
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        break;
      case goalSortableColumns.Outcome:
        if (a.outcome == undefined && b.outcome == undefined) {
          convertedValueA = Number(a.status === GoalStatus.Closed);
          convertedValueB = Number(b.status === GoalStatus.Closed);
        } else {
          convertedValueA =
            a.outcome == undefined ? -1 : a.outcome === GoalOutcome.Met ? 1 : 0;
          convertedValueB =
            b.outcome == undefined ? -1 : b.outcome === GoalOutcome.Met ? 1 : 0;
        }
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        break;
      case goalSortableColumns.TargetValue:
        if (isPercentage(a) !== isPercentage(b)) {
          // one minute value + one percentage value
          // show percentage first
          convertedValueA = Number(isPercentage(a));
          convertedValueB = Number(isPercentage(b));
        } else if (isPercentage(a)) {
          // both percentage
          convertedValueA = Number(a.targetValue);
          convertedValueB = Number(b.targetValue);
        } else {
          // both minute
          convertedValueA = convertToSeconds(a.targetValue);
          convertedValueB = convertToSeconds(b.targetValue);
        }
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        break;
      default:
        convertedValueA = valueA!;
        convertedValueB = valueB!;
        sortResult =
          convertedValueA > convertedValueB
            ? 1
            : convertedValueA < convertedValueB
              ? -1
              : 0;
        return sortDirection === SortDirection.ASCENDING
          ? sortResult
          : -sortResult;
    }
    return sortDirection === SortDirection.ASCENDING ? sortResult : -sortResult;
  };
};

export const getFullNameFromUserID = (
  userID: string,
  profile: Profile
): string => {
  if (userID == profile.user.userID) return profile.user.displayName;
  for (const user of profile.sharedProfileCoachees)
    if (userID === user.userID) return user.displayName;
  return userID;
};

const isPercentage = (goal: GoalDisplay): boolean => {
  return (
    goalMetricMetadataMap[goal.metric].metricUnit == GoalMetricUnit.PERCENTAGE
  );
};

export const getMonthNameFromPeriodValue = (periodValue: string): string => {
  const monthIndex = Number(periodValue.split('-')[1]);
  return getMonthTranslation(monthIndex);
};

export function formatGoalValueWithUnit(
  goal: Goal | GoalDisplay,
  intl: any
): string {
  if (
    goalMetricMetadataMap[goal.metric].metricUnit === GoalMetricUnit.PERCENTAGE
  ) {
    return `${(parseFloat(goal.targetValue) * 100).toFixed(1)} %`;
  } else {
    let wholePart, fractionPart;
    if (goal.targetValue.includes(':')) {
      [wholePart, fractionPart] = goal.targetValue.split(':');
      fractionPart = (fractionPart + '00').slice(0, 2);
    } else {
      wholePart = goal.targetValue;
      fractionPart = '00';
    }
    return `${wholePart}:${fractionPart} ${intl.formatMessage({
      id: 'cs_perfportal_min',
      defaultMessage: 'min',
    })}`;
  }
}

export const formatGoalTime = (goal: Goal | GoalDisplay): object => {
  const [yearStr, periodStr] = goal.periodValue.split('-');

  if (goal.periodType == GoalPeriodType.Month) {
    const year = Number(yearStr);
    const period = Number(periodStr);
    return {
      month: Intl.DateTimeFormat(getLocaleStringFormatted(), {
        month: 'long',
      }).format(new Date(year, period - 1)),
      year: yearStr,
    };
  } else {
    return {
      week: periodStr,
      year: yearStr,
    };
  }
};

export function formatGoalToString(
  goal: Goal | GoalDisplay,
  intl: any
): string {
  let goal_string_id, goal_time_id;
  let default_goal_string, default_goal_time;
  if (goalMetricMetadataMap[goal.metric].isHigherBetter) {
    goal_string_id = 'cs_perfportal_achieve_goal_greater';
    default_goal_string =
      'Achieve {metric} greater than or equal to {value} in {time}';
  } else {
    goal_string_id = 'cs_perfportal_achieve_goal_less';
    default_goal_string =
      'Achieve {metric} less than or equal to {value} in {time}';
  }
  if (goal.periodType === GoalPeriodType.Month) {
    goal_time_id = 'cs_perfportal_achieve_goal_time_month';
    default_goal_time = '{month}, {year}';
  } else {
    goal_time_id = 'cs_perfportal_achieve_goal_time_week';
    default_goal_time = 'Week {week}, {year}';
  }

  return intl.formatMessage(
    {
      id: goal_string_id,
      defaultMessage: default_goal_string,
    },

    {
      value: formatGoalValueWithUnit(goal, intl),
      metric: intl.formatMessage(
        getMetricStringIdAndDefaultMessage(
          goalMetricMetadataMap[goal.metric].metricName
        )
      ),
      time: intl.formatMessage(
        {
          id: goal_time_id,
          defaultMessage: default_goal_time,
        },

        {
          ...formatGoalTime(goal as Goal),
        }
      ),
    }
  );
}

function containsSubstring(
  str: string | undefined | null,
  substr: string
): boolean {
  return str ? str.trim().toLowerCase().includes(substr) : false;
}

export function searchInCoaching(
  form: CoachingFormData,
  substring: string,
  intl: any
): boolean {
  const lowerCaseSubString = substring.trim().toLowerCase();

  // coaching id
  if (containsSubstring(form.coachingId, lowerCaseSubString)) return true;

  // coach
  if (form.deliveredBy) {
    if (containsSubstring(form.deliveredBy.userName, lowerCaseSubString))
      return true;
  } else {
    if (containsSubstring(form.coach.userName, lowerCaseSubString)) return true;
    if (containsSubstring(form.coach.firstName, lowerCaseSubString))
      return true;
    if (containsSubstring(form.coach.fullName, lowerCaseSubString)) return true;
  }

  // timestamps
  if (containsSubstring(form.deliveredAt, lowerCaseSubString)) return true;
  if (containsSubstring(form.closedAt, lowerCaseSubString)) return true;

  // reasons
  if (containsSubstring(form.otherReason, lowerCaseSubString)) return true;
  if (
    containsSubstring(
      getLabelFromValue(form.reason, getAllReasonOptions(intl)) as string,
      lowerCaseSubString
    )
  )
    return true;

  // contact review
  if (form.contactReview) {
    for (const review of form.contactReview) {
      if (containsSubstring(review.cscContactID, lowerCaseSubString))
        return true;
      if (
        containsSubstring(
          getLabelFromValue(
            review.followedSecurityPolicies,
            getAllSecurityOptions(intl)
          ) as string,
          lowerCaseSubString
        )
      )
        return true;
      if (containsSubstring(review.content, lowerCaseSubString)) return true;
      if (
        containsSubstring(
          getLabelFromValue(review.relatedMetric, getAllMetrics()) as string,
          lowerCaseSubString
        )
      )
        return true;
      if (
        containsSubstring(
          getLabelFromValue(review.vertical, getAllVerticals()) as string,
          lowerCaseSubString
        )
      )
        return true;
    }
  }

  // coaching topic
  if (form.coachingDetails) {
    for (const detail of form.coachingDetails) {
      if (
        containsSubstring(
          getLabelFromValue(detail.coachingTopic, getAllTopics(intl)) as string,
          lowerCaseSubString
        )
      )
        return true;
      if (detail.relatedContactDomains) {
        for (const contact of detail.relatedContactDomains) {
          if (containsSubstring(contact.cscContactID, lowerCaseSubString))
            return true;
        }
      }
      if (detail.relatedContacts) {
        for (const contact of detail.relatedContacts) {
          if (containsSubstring(contact, lowerCaseSubString)) return true;
        }
      }
      if (detail.tenets) {
        for (const tenet of detail.tenets) {
          if (
            containsSubstring(
              getLabelFromValue(tenet, getAllTenets(intl)) as string,
              lowerCaseSubString
            )
          )
            return true;
        }
      }
      if (detail.behaviors) {
        for (const behavior of detail.behaviors) {
          if (
            containsSubstring(
              getLabelFromValue(behavior, getAllBehaviors(intl)) as string,
              lowerCaseSubString
            )
          )
            return true;
        }
      }
      if (
        containsSubstring(
          getLabelFromValue(detail.relatedMetric, getAllMetrics()) as string,
          lowerCaseSubString
        )
      )
        return true;
      if (containsSubstring(detail.assignedAction, lowerCaseSubString))
        return true;
    }
  }

  // coaching session
  if (form.coachingSession) {
    if (
      form.coachingSession.managerNotes &&
      containsSubstring(form.coachingSession.managerNotes, lowerCaseSubString)
    )
      return true;
    if (
      form.coachingSession.coachingTactic &&
      containsSubstring(
        getLabelFromValue(
          form.coachingSession.coachingTactic,
          getAllTactics(intl)
        ) as string,
        lowerCaseSubString
      )
    )
      return true;
    if (
      form.coachingSession.coachingMethod &&
      containsSubstring(
        getLabelFromValue(
          form.coachingSession.coachingMethod,
          getAllMethods(intl)
        ) as string,
        lowerCaseSubString
      )
    )
      return true;
    if (
      typeof form.coachingSession.duration == 'string' &&
      containsSubstring(
        getLabelFromValue(
          form.coachingSession.duration,
          getAllDurations()
        ) as string,
        lowerCaseSubString
      )
    )
      return true;
    if (
      typeof form.coachingSession.preparationTime == 'string' &&
      containsSubstring(
        getLabelFromValue(
          form.coachingSession.preparationTime,
          getAllPreparationTimes(intl)
        ) as string,
        lowerCaseSubString
      )
    )
      return true;
    if (
      typeof form.coachingSession.coachingSummaryNotes &&
      containsSubstring(
        form.coachingSession.coachingSummaryNotes,
        lowerCaseSubString
      )
    )
      return true;
  }

  // goals
  if (form.goals) {
    for (const goal of form.goals) {
      if (containsSubstring(formatGoalToString(goal, intl), lowerCaseSubString))
        return true;
    }
  }

  if (form.isDelivered) {
    switch (form.isDelivered) {
      case DeliveredStatusObject.NOCOACH: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_no_session',
              defaultMessage: 'No session',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
      case DeliveredStatusObject.DELIVERED: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_coached',
              defaultMessage: 'Coached',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
      case DeliveredStatusObject.OPEN_DELIVERED: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_open_delivered',
              defaultMessage: 'Open(Delivered)',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
      case DeliveredStatusObject.OPEN_REVIEWED: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_open_reviewed',
              defaultMessage: 'Open(Reviewed)',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
      case DeliveredStatusObject.CLOSED: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_closed',
              defaultMessage: 'Closed',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
      default: {
        if (
          containsSubstring(
            intl.formatMessage({
              id: 'cs_perfportal_coaching_status_not_coached',
              defaultMessage: 'Draft',
            }),
            lowerCaseSubString
          )
        )
          return true;
        break;
      }
    }
  }
  return false;
}
